import React, { useCallback } from 'react';
import classNames from 'classnames';
import validator from 'validator';
import capitalize from 'lodash/capitalize';
import { useQuery } from '@apollo/client';
import { DynamicResultTable, FormattedDate, fetchAuditNotesForEntity } from '@css-xp/base';
import { DEFAULT_ROWS_PER_PAGE } from '@atlassian/cssxp-utils-entitlement';
import { DATE_TIME_FORMAT, NEW_DATE_TIME_FORMAT } from '@atlassian/cssxp-typescommon';
import { emptyStateProp, errorStateProp, NoData, TableHeading } from '@css-xp/ppc-common';
import {
  AuditNote,
  SortDirection,
  FetchAuditNotesForEntityQuery,
  FetchAuditNotesForEntityQueryVariables,
  TermPaymentAuditTrailsSearchOptions,
  TermPaymentAuditTrailSearchParams,
  TermPaymentAuditTrail,
  SortType,
} from '@css-xp/graphtypes/src/generated-types-new';
import { emptyStatePropForAuditNotesTable } from '@css-xp/ppc-common/src/state-props';
import './audit-notes-table.scss';

export interface AuditNotesTableProps {
  id: string;
  label: string;
  shouldShowTermPaymentAuditTrails?: boolean;
  termPaymentAuditTrailsDataState?: any;
  termPaymentSearchOptions?: TermPaymentAuditTrailsSearchOptions;
  termPaymentSearchParams?: TermPaymentAuditTrailSearchParams;
  isDesignIteration1Enabled?: boolean;
  hiddenColumns?: string[];
}

export const COLUMNS = {
  DATE: 'Date',
  UPDATE: 'Update',
  DETAILS: 'Details',
  ACTIONED_BY: 'Actioned by',
};

const BODY_VALUE_TYPE_LIST = {
  // From BE 2 separate keys are available both (Billing_Admin_Updated & BILLING_ADMIN_UPDATED) as case sensitive values
  // which may has different value so once content is given will add it here
  Billing_Admin_Updated: 'Billing admin updated',
  BILLING_ADMIN_UPDATED: 'Billing admin updated',
  BILLING_ADMIN_ADDED: 'Billing admin added',
  BILLING_ADMIN_REMOVED: 'Billing admin removed',
  BILL_TO_UPDATED: 'Billing address updated',
  SHIP_TO_CREATED: 'Sold-to address created',
  SHIP_TO_UPDATED: 'Sold-to address updated',
  SHIP_TO_DEACTIVATED: 'Sold-to address deactivated',
  RECIPIENTS_LIST_UPDATED: 'Recipients list updated',
  PAYMENT_METHOD_CREATED: 'Payment method created',
  PAYMENT_METHOD_UPDATED: 'Payment method updated',
  HUMAN_READABLE_ID_GENERATED: 'Human readable ID generated',
};

const wrapInCell = (node: React.ReactElement | string) => {
  return <div className="audit-note-cell">{node}</div>;
};

const makeColumns = (
  auditNotes: AuditNote[] | undefined,
  hiddenColumns: string[],
  isDesignIteration1Enabled?: boolean
) => {
  return auditNotes?.map((auditNote, index) => {
    const { created, body, author, contactKey } = auditNote;
    const action = body?.split('-');
    const update = action?.[0];
    const updatedBodyValue = BODY_VALUE_TYPE_LIST[update as keyof typeof BODY_VALUE_TYPE_LIST] ?? update;
    let detail;
    if (action && action?.length > 1) {
      detail = action[1];
    }
    return {
      key: `audit-note-v2-${index}`,
      cells: [
        {
          key: COLUMNS.DATE,
          content: wrapInCell(
            created?.toString() ? (
              <FormattedDate date={created?.toString()} showTime={true} format={NEW_DATE_TIME_FORMAT} />
            ) : (
              <NoData />
            )
          ),
        },
        {
          key: COLUMNS.UPDATE,
          content: wrapInCell(<span>{updatedBodyValue ?? <NoData />}</span>),
        },
        { key: COLUMNS.DETAILS, content: wrapInCell(<span>{detail ? detail : contactKey ?? <NoData />}</span>) },
        {
          key: COLUMNS.ACTIONED_BY,
          content: wrapInCell(
            <div
              className={classNames({
                email: !isDesignIteration1Enabled && author && validator.isEmail(author),
              })}
            >
              {author ? author : created && update ? 'System' : <NoData />}
            </div>
          ),
        },
      ].filter((cell) => !hiddenColumns?.includes(cell.key)),
    };
  });
};

const makeTermPaymentAuditColumns = (
  auditNotes: TermPaymentAuditTrail[] | undefined,
  hiddenColumns?: string[],
  isDesignIteration1Enabled?: boolean
) => {
  return auditNotes?.map((auditNote, index) => {
    const { createdAt, revisionType, updatedBy, comments } = auditNote;
    const modifiedRevisionType = capitalize(revisionType?.split('_')?.join(' '));

    return {
      key: `audit-note-v2-${index}`,
      cells: [
        {
          key: COLUMNS.DATE,
          content: wrapInCell(
            <FormattedDate date={createdAt ? Math.floor(createdAt).toString() : ''} format={DATE_TIME_FORMAT} />
          ),
        },
        {
          key: COLUMNS.UPDATE,
          content: wrapInCell(<span>{modifiedRevisionType || <NoData />}</span>),
        },
        {
          key: COLUMNS.DETAILS,
          content: wrapInCell(<span>{comments ?? <NoData />}</span>),
        },
        {
          key: COLUMNS.ACTIONED_BY,
          content: wrapInCell(
            <div
              className={classNames({
                email: !isDesignIteration1Enabled && updatedBy && validator.isEmail(updatedBy ?? ''),
              })}
            >
              {updatedBy ?? <NoData />}
            </div>
          ),
        },
      ].filter((cell) => !hiddenColumns?.includes(cell.key)),
    };
  });
};

export const getDateSortType = (isDateAscSorted?: boolean): SortType => {
  return {
    sortFields: ['created', 'id'],
    sortFieldsDirections: [isDateAscSorted ? SortDirection.ASC : SortDirection.DESC, SortDirection.DESC],
  };
};

export const defaultSortOptions: SortType = getDateSortType();

export const defaultAuditNotesSearchOptions: FetchAuditNotesForEntityQueryVariables['searchOptions'] = {
  first: DEFAULT_ROWS_PER_PAGE,
  sort: defaultSortOptions,
};

export const AuditNotesTable = ({
  id,
  label,
  shouldShowTermPaymentAuditTrails,
  termPaymentAuditTrailsDataState,
  isDesignIteration1Enabled,
  hiddenColumns = [],
}: AuditNotesTableProps) => {
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(DEFAULT_ROWS_PER_PAGE);
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const {
    data: termPaymentAuditTrailData,
    error: termPaymentAuditTrailError,
    loading: termPaymentAuditTrailLoading,
    refetch: termPaymentAuditTrailRefetch,
  } = termPaymentAuditTrailsDataState || {};
  const [sortOrder, setSortOrder] = React.useState<SortDirection>(SortDirection.DESC);
  const [sortKey, setSortKey] = React.useState<string>('Date');
  const [sortOptions, setSortOptions] = React.useState<SortType>(defaultSortOptions);

  const searchParams = {
    and: [{ transactionAccountKey: id }, { idText: 'transaction-account' }],
  };

  const { data, loading, error, refetch } = useQuery<
    FetchAuditNotesForEntityQuery,
    FetchAuditNotesForEntityQueryVariables
  >(fetchAuditNotesForEntity, {
    variables: {
      searchParams,
      searchOptions: defaultAuditNotesSearchOptions,
    },
    displayName: 'fetchAuditNotesByEntityId',
    notifyOnNetworkStatusChange: true,
    skip: shouldShowTermPaymentAuditTrails,
  });

  const queryResults = shouldShowTermPaymentAuditTrails ? termPaymentAuditTrailData : data?.auditNotes;
  const auditLogs = shouldShowTermPaymentAuditTrails
    ? (queryResults?.nodes as TermPaymentAuditTrail[])
    : (queryResults?.nodes as AuditNote[]);

  const totalCount = queryResults?.totalCount ?? 0;

  const makeHead = () => {
    if (totalCount === 0) {
      return { cells: [] };
    }
    let cells;

    if (!isDesignIteration1Enabled) {
      cells = Object.values(COLUMNS)?.map((column) => {
        return {
          key: column,
          content: wrapInCell(column),
        };
      });
    } else {
      cells = [
        {
          key: COLUMNS.DATE,
          content: COLUMNS.DATE,
          width: 50,
          isSortable: !shouldShowTermPaymentAuditTrails, // as we don't need sorting functionality in credit details page
        },
        {
          key: COLUMNS.UPDATE,
          content: wrapInCell(COLUMNS.UPDATE),
          width: 45,
        },
        {
          key: COLUMNS.DETAILS,
          content: wrapInCell(COLUMNS.DETAILS),
          width: 70,
        },
        {
          key: COLUMNS.ACTIONED_BY,
          content: wrapInCell(COLUMNS.ACTIONED_BY),
          width: 100,
        },
      ];

      cells = cells.filter((cell) => !hiddenColumns?.includes(cell.key));
    }

    return {
      cells,
    };
  };

  const rows = error
    ? []
    : shouldShowTermPaymentAuditTrails
    ? makeTermPaymentAuditColumns(auditLogs as TermPaymentAuditTrail[], hiddenColumns, isDesignIteration1Enabled)
    : makeColumns(auditLogs as AuditNote[], hiddenColumns, isDesignIteration1Enabled);

  const onRowsPerPageChange = useCallback(
    (rowsCount: number) => {
      setRowsPerPage(rowsCount);
      shouldShowTermPaymentAuditTrails
        ? termPaymentAuditTrailRefetch({
            searchOptions: {
              page: 0,
              pageSize: rowsCount,
            },
          })
        : refetch({
            searchOptions: {
              first: rowsCount,
              sort: sortOptions,
            },
          });
      setCurrentPage(1);
    },
    [sortOptions, shouldShowTermPaymentAuditTrails, termPaymentAuditTrailRefetch, refetch]
  );

  const onPageChange = React.useCallback(
    (newPage: number) => {
      setCurrentPage(newPage);
      shouldShowTermPaymentAuditTrails
        ? termPaymentAuditTrailRefetch({
            searchOptions: {
              page: newPage - 1,
              pageSize: rowsPerPage,
            },
          })
        : refetch({
            searchOptions: {
              first: rowsPerPage,
              offset: (newPage - 1) * rowsPerPage,
              sort: sortOptions,
            },
          });
    },
    [rowsPerPage, refetch, termPaymentAuditTrailRefetch, sortOptions, shouldShowTermPaymentAuditTrails]
  );

  const handleSorting = (data: any) => {
    setSortOrder(data.sortOrder);
    setSortKey(data.key);
    setCurrentPage(1);
    const newSortOptions = getDateSortType(data.sortOrder === SortDirection.ASC);
    setSortOptions(newSortOptions);

    shouldShowTermPaymentAuditTrails
      ? termPaymentAuditTrailRefetch({
          searchOptions: {
            page: currentPage,
            pageSize: rowsPerPage,
          },
        })
      : refetch({
          searchOptions: {
            first: rowsPerPage,
            sort: newSortOptions,
          },
        });
  };

  const showError = shouldShowTermPaymentAuditTrails ? termPaymentAuditTrailError : error?.toString();
  const isLoading = shouldShowTermPaymentAuditTrails ? termPaymentAuditTrailLoading : loading;

  return (
    <div className="audit-notes-table-container" data-testid="audit-notes-txa-log">
      <div className="custom-design">
        <DynamicResultTable
          beforeTable={isDesignIteration1Enabled ? null : <TableHeading>{label}</TableHeading>}
          head={makeHead()}
          rows={rows}
          loadingSpinnerSize="small"
          totalCount={totalCount}
          emptyStateProps={isDesignIteration1Enabled ? emptyStatePropForAuditNotesTable : emptyStateProp}
          errorStateProps={errorStateProp}
          error={showError}
          isLoading={isLoading}
          isPaginationEnabled={totalCount > DEFAULT_ROWS_PER_PAGE}
          rowsPerPage={rowsPerPage}
          currentPage={currentPage}
          onPageChange={onPageChange}
          onRowChange={onRowsPerPageChange}
          sortKey={isDesignIteration1Enabled && totalCount > 0 ? sortKey : undefined}
          sortOrder={isDesignIteration1Enabled ? sortOrder : undefined}
          onSort={isDesignIteration1Enabled ? handleSorting : undefined}
          className={isDesignIteration1Enabled ? 'audit-notes-table-v2' : ''}
        ></DynamicResultTable>
      </div>
    </div>
  );
};
